export const mdmDeepEqual = <T>(
  a: T,
  b: T,
  seen: WeakMap<object, object> = new WeakMap(),
): boolean => {
  if (a === b) return true;

  if (
    a === null ||
    b === null ||
    typeof a !== "object" ||
    typeof b !== "object"
  ) {
    return Number.isNaN(a) && Number.isNaN(b);
  }

  if (seen.has(a as object)) return seen.get(a as object) === b;
  seen.set(a as object, b as object);

  if (a instanceof Date && b instanceof Date)
    return a.getTime() === b.getTime();

  if (a instanceof RegExp && b instanceof RegExp)
    return a.source === b.source && a.flags === b.flags;

  // Map
  if (a instanceof Map && b instanceof Map) {
    if (a.size !== b.size) return false;
    const entriesA = Array.from(a.entries());
    return entriesA.every(([key, valA]) => {
      if (!b.has(key)) return false;
      const valB = b.get(key);
      return mdmDeepEqual(valA, valB, seen);
    });
  }

  // Set
  if (a instanceof Set && b instanceof Set) {
    if (a.size !== b.size) return false;
    const valuesA = Array.from(a.values());
    const valuesB = Array.from(b.values());
    return valuesA.every((valA) =>
      valuesB.some((valB) => mdmDeepEqual(valA, valB, seen)),
    );
  }

  if (Object.getPrototypeOf(a) !== Object.getPrototypeOf(b)) return false;

  const keysA = Reflect.ownKeys(a as object);
  const keysB = Reflect.ownKeys(b as object);
  if (keysA.length !== keysB.length) return false;

  return keysA.every((key) => {
    if (!keysB.includes(key)) return false;
    const valA = (a as Record<PropertyKey, unknown>)[key];
    const valB = (b as Record<PropertyKey, unknown>)[key];
    return mdmDeepEqual(valA, valB, seen);
  });
};
